import { Address, AddressData, AddressHelper } from "@app/address/model/address";
import { EntityInfoData, EntityInfoTypes } from "@app/entity/model/entityinfo";
import { DbCrudHelper, RowsAffected } from "@app/shared/services/db-crud-helper";
import { catchError, map } from "rxjs/operators";

import { Injectable } from "@angular/core";
import { ClareWSCrudHelperService } from "@app/shared/services/clare-ws-crud-helper.service";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";
import { Observable } from "rxjs";

export interface EntityinfoAddressPayload {
    address_id: number;
    entityinfo_id: number;
}

// The getAdddress call returns some extra gunk that we restructure into the Address object
interface AddressEntityInfo {
    id: number;
    data: string; // This is a stringified AddressEntityInfoData
    key: string;
}
interface AddressEntityInfoData {
    name: string;
}

@Injectable({
    providedIn: "root",
})
export class AddressDbService {
    helper = new AddressHelper();
    wsHelper = new ClareWSCrudHelperService(this.tsLoggingService, this.crudHelper, this.helper);

    constructor(private readonly tsLoggingService: TSLoggingService, private readonly crudHelper: DbCrudHelper) {}

    getAddress(id: number) {
        return this.crudHelper
            .makeRequest<AddressData & { Entityinfo: AddressEntityInfo[] | null }>("GetAddressAndEntityinfos", { id })
            .pipe(
                map(data => {
                    const address = this.helper.fromDbObject(data);

                    data.Entityinfo?.forEach(info => {
                        const { name } = JSON.parse(info.data) as AddressEntityInfoData;
                        switch (info.key) {
                            case EntityInfoTypes.HomeBuilders:
                                address.builderid = info.id;
                                address.builderName = name;
                                break;
                            case EntityInfoTypes.BuildingType:
                                address.typeid = info.id;
                                address.buildingTypeName = name;
                                break;
                            case EntityInfoTypes.HomeCommunities:
                                address.communityid = info.id;
                                address.communityName = name;
                                break;
                            default:
                                console.error("Ignoring unexpected entry in Entityinfo", info);
                        }
                    });

                    return address;
                }),
                catchError(DataServiceError.handleError())
            );
    }

    createAddress(address: Address, addressType: string, entityId: number): Observable<Address> {
        // There is no longer a createAddress call. We now have separate calls for creating shipping and billing addresses.
        // Creating project addresses are now done through the create project api call
        const addressData = AddressHelper.toDbObject(address);
        return addressType === "billingAddress"
            ? this.createBillingAddress(addressData, entityId)
            : this.createShippingAddress(addressData, entityId);
    }

    createBillingAddress(addressData: AddressData, entityId: number): Observable<Address> {
        return this.crudHelper
            .makeRequest<Address>("CreateBillingAddress", { address: addressData, entity_id: entityId })
            .pipe(catchError(DataServiceError.handleError()));
    }

    createShippingAddress(addressData: AddressData, entityId: number): Observable<Address> {
        return this.crudHelper
            .makeRequest<Address>("CreateShippingAddress", { address: addressData, entity_id: entityId })
            .pipe(catchError(DataServiceError.handleError()));
    }

    updateAddress(address: Address) {
        return this.wsHelper.update({ endpoint: "UpdateAddress", data: address, logData: address });
    }

    deleteAddress(address: Address) {
        return this.wsHelper.delete({ endpoint: "DeleteAddress", id: address.id, logData: address });
    }

    createEntityinfoAddress(payload: EntityinfoAddressPayload): Observable<EntityInfoData> {
        return this.crudHelper
            .makeRequest<EntityInfoData>("CreateEntityinfoAddress", payload)
            .pipe(catchError(DataServiceError.handleError()));
    }

    deleteEntityinfoAddress(payload: EntityinfoAddressPayload) {
        this.crudHelper
            .makeRequest<RowsAffected>("DeleteEntityinfoAddress", payload)
            .pipe(this.crudHelper.throwIfZeroUpdates(), catchError(DataServiceError.handleError()));
    }
}
